import React, { useState, useRef } from 'react';
import { useSelector } from 'react-redux';
import styled, { css } from 'styled-components';
import OptionsSelector from '@panel/commons/lib/DropDown/OptionSelector';
import { select } from '@panel/commons/core/themev2';
import { getIsTransitionOrBundleCredit } from '@panel/commons/helpers';
import { selectColor, sendTrackingInfo } from '@panel/commons';
import cardNoImg from '@panel/commons/assets/icons/aviso-sin-foto.svg';
import { useTranslation } from 'react-i18next';
import { genericFormatAmount } from '@panel/commons/helpers/formatAmount';
import Retry from './Retry';
import { DesempenoOperations, DesempenoSelectors } from '../../ducks/desempeno';
import Quality from './Quality';
import Dropdown from './Dropdown';

const PostingCardPreview = ({
  posting,
  device,
  postingId,
  postingQuality,
  setShowModal,
  showModal,
  isDuplicate,
  isPenalized,
  isStatisticsByPosting,
  showSelectPosting,
  setShowSelectPosting,
  hasSelectorPosting,
  optionsPostingsArray,
  handleOutsideClick,
  withoutQuality,
  withoutRedirect
}) => {
  const { t } = useTranslation();
  const [qualityDropdown, setQualityDropdown] = useState(false);
  const { postingError } = useSelector(DesempenoSelectors.getDesempenoState);
  const { getPostingInfo } = DesempenoOperations;
  const { title, urlPicture, address, status, urlPosting, publicationPlanId, publicationPlanName } = posting;
  const priceOperationType = posting.price_operationtype;
  const realestateType = posting.realestate_type;
  const isTransitionOrBundleCredit = getIsTransitionOrBundleCredit(parseInt(publicationPlanId));
  const publicationPlan = posting.publication_plan ? t(`panel.avisos.planName.${posting.publication_plan}`) : '';
  const validations = postingQuality && postingQuality.validations;
  const percentageCorrectness = postingQuality && postingQuality.percentage_correctness;
  const qualityStatus = postingQuality && postingQuality.quality_status;
  const qualityNotApply = !qualityStatus || qualityStatus === 'not_apply';
  const positionSelectRef = useRef(null);

  const getQualityText = () => {
    if (isDuplicate) return t('panel.avisos.posting.quality.duplicated');
    if (isPenalized) return t('panel.avisos.posting.quality.penalized');
    if (qualityNotApply) return t('panel.avisos.posting.quality.notApply');
    if (percentageCorrectness >= 0 && !qualityNotApply) return t('panel.avisos.posting.quality.ok');
    return t('panel.avisos.posting.quality.withoutData');
  };

  const getQualityMessage = () => {
    if (isDuplicate) return t('panel.avisos.posting.quality.duplicated');
    if (isPenalized) return t('panel.avisos.posting.quality.penalized');
    return t(`panel.posting.quality.${!qualityNotApply && qualityStatus ? qualityStatus : 'not_apply'}`);
  };

  const getTooltipText = () => {
    if (isDuplicate) return t('panel.avisos.posting.tooltip.isDuplicated');
    if (isPenalized) return t('panel.avisos.posting.tooltip.isPenalized');
    if (qualityStatus === 'not_apply')
      return t('panel.avisos.posting.tooltip.withoutData', { postingType: realestateType });
    if (percentageCorrectness === 100) return t('panel.avisos.posting.tooltip.complete');
    return t('panel.avisos.posting.tooltip.improvePosting');
  };

  const getFormatedPrices = () => {
    const prices = [];
    if (priceOperationType) {
      priceOperationType.forEach(operation => {
        if (operation) {
          const { amountAndCurrency } = operation;
          prices.push({
            operationType: operation.operationType,
            price: amountAndCurrency && genericFormatAmount(amountAndCurrency[0].amount, amountAndCurrency[0].currency),
            secondPrice:
              amountAndCurrency && amountAndCurrency[1]
                ? genericFormatAmount(amountAndCurrency[1].amount, amountAndCurrency[1].currency)
                : ''
          });
        }
      });
    }
    return prices;
  };
  const priceList = getFormatedPrices();
  const redirectUrl = `${window.location.origin}/${t('panel.url.property')}/${urlPosting}`;

  const handleRedirectToPosting = () => {
    sendTrackingInfo('Panel', 'Mis avisos', 'Desempeño del aviso-Ir al aviso');
    window.open(redirectUrl, '_blank');
  };

  const tooltipComponent = text => {
    return <TooltipComponent>{text}</TooltipComponent>;
  };

  return (
    <>
      <Container className={`desempeno-card-preview-${device}`}>
        {postingError && <Retry height='60%' actionsToRetry={[getPostingInfo]} postingId={postingId} />}
        {!postingError && (
          <ImageContainer data-panel='image-cardPreview'>
            <img
              src={urlPicture}
              alt='Posting'
              onError={({ currentTarget }) => {
                currentTarget.onerror = null; // previene looping
                currentTarget.src = cardNoImg;
                currentTarget.style = `background-color:${select('grayMedium')}; object-fit:none; border-radius:8px`;
              }}
            />
            {posting.isReserved && <ReservedTag isMobile={device === 'mobile'}>Reservado</ReservedTag>}
          </ImageContainer>
        )}
        {!postingError && (
          <InfoArea device={device} className={`desempeno-card-preview-${device}`}>
            <TypeAndStatus device={device}>
              {realestateType && <RealEstateType data-panel='realEstate-type'>{realestateType}</RealEstateType>}
              {status && (
                <StatusTag data-panel='status-post' type={status}>
                  <div />
                  {t(`panel.posting.status.${status}`)}
                </StatusTag>
              )}
            </TypeAndStatus>
            <Title
              className={`desempeno-card-preview-${device}`}
              data-panel='title-card-preview'
              withoutRedirect={withoutRedirect}
              onClick={() => {
                if (!hasSelectorPosting && !withoutRedirect) handleRedirectToPosting();
                if (hasSelectorPosting && !withoutRedirect) {
                  setShowSelectPosting({ toBackButton: false, toTitle: !showSelectPosting.toTitle });
                }
              }}
            >
              {title}
            </Title>
            {hasSelectorPosting && showSelectPosting.toTitle && (
              <OptionsSelector
                isOpen={showSelectPosting.toTitle}
                optionsArray={optionsPostingsArray}
                elementRef={positionSelectRef}
                handleOutsideClick={handleOutsideClick}
                height='94px'
                topDown={device !== 'mobile' ? '66px' : '55px'}
                left='unset'
                right='unset'
              />
            )}
            <Address className={`desempeno-card-preview-${device}`} data-panel='address-card-preview'>
              {address}
            </Address>
            <MiddleArea className={`desempeno-card-preview-${device}`}>
              <PriceArea className={`desempeno-card-preview-${device}`}>
                <PriceArea>
                  {device === 'tablet' &&
                    priceList.map(item => (
                      <InfoItem
                        key={`price-${item.operationType}`}
                        className={`price-tag desempeno-card-preview-${device}`}
                        data-panel='container-price-card-preview'
                      >
                        <span data-panel='type-price-card-preview'>{item.operationType}</span>
                        <span data-panel='price-card-preview'>
                          {`${item.price === 0 ? t('panel.posting.no.price') : item.price}
                          ${
                            item.secondPrice
                              ? `/ ${item.secondPrice === 0 ? t('panel.posting.no.price') : item.secondPrice}`
                              : ''
                          }`}
                        </span>
                      </InfoItem>
                    ))}
                  {device === 'desktop'
                    ? priceList.map(item => (
                        <InfoItem
                          key={`price-${item.operationType}`}
                          className={`price-tag desempeno-card-preview-${device}`}
                          data-panel='container-price-card-preview'
                        >
                          <span data-panel='type-price-card-preview'>{item.operationType}</span>
                          <span data-panel='price-card-preview'>
                            {`${item.price === 0 ? t('panel.posting.no.price') : item.price}`}
                            <br />
                            {`${
                              item.secondPrice
                                ? `${item.secondPrice === 0 ? t('panel.posting.no.price') : item.secondPrice}`
                                : ''
                            }`}
                          </span>
                        </InfoItem>
                      ))
                    : device === 'mobile' &&
                      priceList[0] && (
                        <InfoItem
                          key={`price-${priceList[0].operationType}`}
                          className={`price-tag desempeno-card-preview-${device}`}
                          data-panel='container-price-card-Preview'
                        >
                          <span data-panel='type-price-card-Preview'>{priceList[0].operationType}</span>
                          <span data-panel='price-card-Preview'>{`${
                            priceList[0].price === 0 ? t('panel.posting.no.price') : priceList[0].price
                          }${
                            priceList[0].secondPrice
                              ? ` / ${
                                  priceList[0].secondPrice === 0
                                    ? t('panel.posting.no.price')
                                    : priceList[0].secondPrice
                                }`
                              : ''
                          }`}</span>
                        </InfoItem>
                      )}
                </PriceArea>
                {device !== 'desktop' && (
                  <InfoItem
                    className={`desempeno-card-preview-${device} desempeno-plan-area`}
                    data-panel='container-plan-card-preview-mobile'
                  >
                    <span data-panel='title-plan-card-preview-mobile'>{t('panel.desempeno.plan.titulo')}</span>
                    <span data-panel='plan-card-preview-mobile'>
                      {isTransitionOrBundleCredit ? publicationPlanName : publicationPlan}
                    </span>
                  </InfoItem>
                )}
              </PriceArea>
              {device === 'desktop' && (
                <PlanAndQuality>
                  <InfoItem
                    className={`desempeno-card-preview-${device} desempeno-plan-area`}
                    data-panel='container-plan-card-preview'
                  >
                    <span data-panel='title-plan-card-preview'>{t('panel.desempeno.plan.titulo')}</span>
                    <span data-panel='plan-card-preview'>
                      {isTransitionOrBundleCredit ? publicationPlanName : publicationPlan}
                    </span>
                  </InfoItem>
                  {!withoutQuality && (
                    <QualityArea data-panel='panel-card-preview-quality'>
                      <Quality
                        quality={percentageCorrectness}
                        qualityNotApply={qualityNotApply}
                        device={device}
                        qualityStatus={qualityStatus}
                        qualityText={getQualityText()}
                        tooltipText={
                          isPenalized || qualityStatus === 'not_apply'
                            ? tooltipComponent(getTooltipText())
                            : getTooltipText()
                        }
                        isDuplicate={isDuplicate}
                        isPenalized={isPenalized}
                        validations={validations}
                        openModal={() => {
                          // eslint-disable-next-line no-unused-expressions
                          validations &&
                            validations.length > 0 &&
                            setShowModal({ ...showModal, performanceTips: true });
                          sendTrackingInfo('Panel', 'Mis avisos', 'Desempeño del aviso-Mejora tu calidad');
                        }}
                      />
                    </QualityArea>
                  )}
                </PlanAndQuality>
              )}
            </MiddleArea>
          </InfoArea>
        )}
      </Container>
      {device !== 'desktop' && !isStatisticsByPosting && !withoutQuality && (
        <Dropdown title={t('panel.desempeno.quality.title')} isOpen={qualityDropdown} setIsOpen={setQualityDropdown}>
          <QualityDropdownContainer>
            <Quality
              quality={percentageCorrectness}
              qualityNotApply={qualityNotApply}
              device={device}
              qualityStatus={qualityStatus}
              qualityText={getQualityText()}
              isDuplicate={isDuplicate}
              isPenalized={isPenalized}
              tooltipText={qualityStatus === 'not_apply' ? tooltipComponent(getTooltipText()) : null}
            />
            <QualityRight isDuplicate={isDuplicate} isPenalized={isPenalized} qualityStatus={qualityStatus}>
              <div>
                {qualityStatus === 'not_apply'
                  ? t('panel.avisos.posting.tooltip.withoutData', { postingType: realestateType })
                  : getQualityMessage()}
              </div>
              {validations && validations.length > 0 && (
                <div
                  onClick={() => {
                    // eslint-disable-next-line no-unused-expressions
                    setShowModal({ ...showModal, performanceTips: true });
                    sendTrackingInfo('Panel', 'Mis avisos', 'Desempeño del aviso-Mejora tu calidad');
                  }}
                  data-panel='panel-cardPreview-quality'
                >
                  {t('panel.avisos.posting.calidad.mejora')}
                </div>
              )}
            </QualityRight>
          </QualityDropdownContainer>
        </Dropdown>
      )}
    </>
  );
};

const Container = styled.div`
  width: 290px;
  border-radius: 8px;
  border: 1px solid ${select('grayMedium')};
  display: flex;
  flex-direction: column;
  box-shadow: 0px 8px 16px rgba(0, 0, 0, 0.08);
  background-color: white;
  img {
    height: 152px;
    width: 100%;
    border-top-left-radius: 8px;
    border-top-right-radius: 8px;
    object-fit: none;
  }
  &.desempeno-card-preview-desktop {
    min-height: 250px;
  }
  &.desempeno-card-preview-tablet,
  &.desempeno-card-preview-mobile {
    flex-direction: row;
    width: 100%;
    box-shadow: none;
    position: relative;
    top: 0;
  }
  &.desempeno-card-preview-tablet {
    height: 188px;
    img {
      height: 100%;
      width: 189px;
      border-top-right-radius: 0;
      border-bottom-left-radius: 8px;
    }
  }
  &.desempeno-card-preview-mobile {
    height: 143px;
    img {
      height: 82px;
      width: 82px;
      border-radius: 8px;
      margin: 30px 0 30px 12px;
    }
  }
  @media screen and (max-width: 1280px) {
    top: 100px;
  }
`;

const InfoArea = styled.div`
  display: flex;
  flex-direction: column;
  flex: 1;
  padding: 17px 19px 9px 19px;
  position: relative;
  &.desempeno-card-preview-tablet {
    padding: 14px 16px;
    min-width: 0;
  }
  &.desempeno-card-preview-mobile {
    padding: 14px 16px;
    min-width: 0;
  }
`;

const TypeAndStatus = styled.div`
  width: 100%;
  display: flex;
  justify-content: space-between;
  margin-bottom: ${({ device }) => (device === 'mobile' ? '4px' : '12px')};
`;

const RealEstateType = styled.div`
  font-family: Hind;
  font-size: 12px;
  font-style: normal;
  font-weight: 500;
  line-height: 19px;
  color: ${select('darkGray')};
`;

const Title = styled.div`
  text-decoration: ${({ withoutRedirect }) => (withoutRedirect ? 'none' : 'underline')};
  cursor: ${({ withoutRedirect }) => (withoutRedirect ? 'auto' : 'pointer')};
  max-width: 100%;
  font-family: Hind;
  font-size: 16px;
  font-style: normal;
  font-weight: 600;
  line-height: 21px;
  margin-bottom: 6px;
  overflow: hidden;
  text-overflow: ellipsis;
  &.desempeno-card-preview-tablet {
    font-family: Hind;
    font-size: 18px;
    font-style: normal;
    font-weight: 600;
    line-height: 23px;
  }
  &.desempeno-card-preview-mobile {
    font-family: Hind;
    font-size: 14px;
    font-style: normal;
    font-weight: 600;
    line-height: 18px;
    margin-bottom: 3px;
  }
`;

const Address = styled.div`
  width: 100%;
  overflow: hidden;
  text-overflow: ellipsis;
  font-family: Hind;
  font-size: 14px;
  font-style: normal;
  font-weight: 400;
  line-height: 22px;
  color: ${select('midDarkGray')};
  margin-bottom: 15px;
  &.desempeno-card-preview-tablet {
    margin-bottom: 10px;
  }
  &.desempeno-card-preview-mobile {
    font-size: 12px;
    line-height: 19px;
    margin-bottom: 4px;
  }
`;

const MiddleArea = styled.div`
  width: 100%;
  display: flex;
  justify-content: space-between;
  flex: 1;
  padding-bottom: 8px;
  &.desempeno-card-preview-desktop {
    flex-direction: column;
  }
  &.desempeno-card-preview-tablet {
    padding-bottom: 0;
  }
`;

const PriceArea = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  width: 100%;
  &.desempeno-card-preview-tablet {
    flex-direction: row;
    margin-right: 0;
    justify-content: flex-start;
  }
`;

const PlanAndQuality = styled.div`
  display: flex;
  justify-content: space-between;
  width: 100%;
  align-items: flex-end;
`;

const InfoItem = styled.div`
  display: flex;
  > span {
    font-family: Hind;
    font-size: 12px;
    font-style: normal;
    font-weight: 400;
    line-height: 19px;
    color: ${select('darkGray')};
    :first-of-type {
      min-width: 55px;
    }
    :last-of-type {
      margin-left: 8px;
      font-family: Hind;
      font-size: 14px;
      font-style: normal;
      font-weight: 600;
      line-height: 18px;
      color: black;
      overflow: hidden;
      text-overflow: ellipsis;
    }
  }
  &.price-tag {
    margin-bottom: 6.5px;
    :last-of-type {
      margin-bottom: 4px;
    }
  }
  &.desempeno-card-preview-desktop {
    &.desempeno-plan-area {
      > span {
        :last-of-type {
          max-width: 140px;
        }
      }
    }
  }
  &.desempeno-card-preview-tablet {
    > span {
      font-family: Hind;
      font-size: 16px;
      font-style: normal;
      font-weight: 400;
      line-height: 26px;
      color: ${select('darkGray')};
      :last-of-type {
        margin-left: 8px;
        font-family: Hind;
        font-size: 16px;
        font-style: normal;
        font-weight: 600;
        line-height: 26px;
        color: black;
        overflow: hidden;
        text-overflow: ellipsis;
      }
    }
    &.price-tag {
      align-items: center;
      margin-bottom: 0;
      > span {
        :first-of-type {
          min-width: 55px;
        }
      }
    }
    &.desempeno-plan-area {
      margin: 0 auto;
    }
  }
  &.desempeno-card-preview-mobile {
    > span {
      font-family: Hind;
      font-size: 12px;
      font-style: normal;
      font-weight: 500;
      line-height: 19px;
      color: ${select('darkGray')};
      :last-of-type {
        margin-left: 8px;
        font-family: Hind;
        font-size: 12px;
        font-style: normal;
        font-weight: 500;
        line-height: 19px;
        text-align: left;
        color: black;
        overflow: hidden;
        text-overflow: ellipsis;
      }
    }
    &.price-tag {
      align-items: center;
      margin-bottom: 4px;
      > span {
        :first-of-type {
          min-width: 10px;
        }
      }
    }
  }
`;

const QualityArea = styled.div`
  display: flex;
  align-items: flex-end;
  justify-content: flex-end;
`;

const StatusActive = css`
  color: ${selectColor('accessibleGreen')};
  div {
    background-color: ${selectColor('accessibleGreen')};
  }
`;

const StatusNearEnd = css`
  color: ${selectColor('brownYellow')};
  div {
    background-color: ${selectColor('brownYellow')};
  }
`;

const StatusFinalized = css`
  color: ${selectColor('feedbackWrong')};
  div {
    background-color: ${selectColor('feedbackWrong')};
  }
`;

const StatusDefault = css`
  color: ${selectColor('grayDark')};
  div {
    background-color: ${selectColor('grayDark')};
  }
`;

const statusFilter = status => {
  if (status === 'ONLINE') return StatusActive;
  if (status === 'NEAR_END') return StatusNearEnd;
  if (status === 'OFFLINE' || status === 'WITHOUT_CREDIT') return StatusFinalized;
  return StatusDefault;
};

const StatusTag = styled.div`
  ${({ type }) => statusFilter(type)}
  width: fit-content;
  font-size: 12px;
  max-width: 97px;
  font-weight: 500;
  display: flex;
  align-items: center;
  div {
    width: 6px;
    height: 6px;
    border-radius: 50%;
    margin-right: 5px;
  }
`;

const QualityDropdownContainer = styled.div`
  width: 100%;
  height: 130px;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: ${({ hasTips }) => hasTips && 'pointer'};
`;

const QualityColors = {
  ok: select('accessibleGreen'),
  warn: select('yellow'),
  error: select('negative'),
  not_apply: select('darkGray')
};

const QualityRight = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  margin-left: 40px;
  max-width: 60%;
  > div {
    font-family: Hind;
    font-size: 18px;
    font-style: normal;
    font-weight: 600;
    line-height: 23px;
    white-space: break-spaces;
    color: ${({ qualityStatus, isDuplicate, isPenalized }) =>
      isDuplicate || isPenalized ? select('darkGray') : QualityColors[qualityStatus]};
    :nth-of-type(2) {
      font-family: Hind;
      font-size: 14px;
      font-style: normal;
      font-weight: 600;
      line-height: 18px;
      letter-spacing: 0em;
      color: black;
      text-decoration: underline;
      margin-top: 6px;
      cursor: pointer;
    }
  }
`;

const ImageContainer = styled.div`
  position: relative;
`;

const ReservedTag = styled.div`
  position: absolute;
  height: 20px;
  font-family: 'Hind';
  font-style: normal;
  font-weight: 500;
  font-size: 12px;
  line-height: 19px;
  border-radius: 23px;
  background-color: ${select('negative')};
  color: white;
  left: 8px;
  top: 8px;
  padding: 4px 8px;
  display: flex;
  justify-content: center;
  align-items: center;
  ${({ isMobile }) =>
    isMobile &&
    css`
      font-size: 10px;
      left: 22px;
      top: 34px;
    `}
`;

const TooltipComponent = styled.div`
  width: 200px;
  white-space: pre-wrap;
  padding: 3px 0;
`;

export default PostingCardPreview;
